import { imageTypes } from '@returnmates/client-core/src/constants'
import * as actions from '@returnmates/client-core/src/constants/actionTypes'
import { Media as TMedia } from '@returnmates/client-core/src/graphql/generated/api'
import { SnackBarStatuses } from '@returnmates/client-core/src/type'
import checkIsBucket from '@returnmates/client-core/src/utils/checkIsBucket'
import errorMapper from '@returnmates/client-core/src/utils/errorMapper'
import { createAsyncAction } from '@returnmates/client-core/src/utils/reduxUtils'
import FileIcon from '@returnmates/ui-core/src/components/images/file'
import ButtonSpinner from '@returnmates/ui-core/src/components/Spinner/ButtonSpinner'
import { memo, useCallback, useEffect, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'

import useStyles from '../styles'

interface Props {
  media?: (TMedia | null)[]
}

interface SignedMedia {
  originUrl: string
  signedUrl: string
  isImage: boolean
}

function Media({ media }: Props) {
  const classes = useStyles()
  const dispatch = useDispatch()
  const [localUrls, setLocalUrls] = useState<SignedMedia[]>([])
  const cashedUrls = useRef<Array<{ originUrl: string; signedUrl: string; isImage: boolean }>>()

  const openFile = useCallback(
    (signedUrl: string) => () => {
      if (signedUrl) {
        window.open(signedUrl, '_blank')?.focus()
      }
    },
    [],
  )

  const signUrls = useCallback(
    async (media?: (TMedia | null)[]) => {
      if (typeof media !== 'object') {
        return
      }

      const newLocalUrls = await Promise.all(
        media.map(async mediaItem => {
          try {
            const existLocalUrl = cashedUrls.current?.find(url => url.originUrl === mediaItem?.url)
            if (existLocalUrl) {
              return existLocalUrl
            }

            const isBucket = checkIsBucket(mediaItem?.url || '')
            const extension = mediaItem?.url?.split('.').pop() || ''
            const isImage = imageTypes.includes(extension)

            if (!isBucket) {
              return {
                isImage,
                signedUrl: mediaItem?.url,
                originUrl: mediaItem?.url,
              }
            }

            const signedUrl: string = await createAsyncAction(
              dispatch,
              actions.signUrl.request(mediaItem?.url),
            )

            return {
              isImage,
              signedUrl,
              originUrl: mediaItem?.url,
            }
          } catch (err) {
            const { message } = err as Error

            dispatch(
              actions.addSnackBar.request({
                type: SnackBarStatuses.ERROR,
                message: errorMapper(message || (err as string)),
              }),
            )
          }
        }),
      )

      if (newLocalUrls) {
        const filteredUrls = newLocalUrls.filter(val => val) as Array<{
          originUrl: string
          signedUrl: string
          isImage: boolean
        }>

        cashedUrls.current = filteredUrls

        setLocalUrls(filteredUrls)
      }
    },
    [dispatch],
  )

  useEffect(() => {
    if (media?.length !== localUrls.length) {
      signUrls(media)
    }
  }, [signUrls, localUrls.length, media])

  return (
    <div className={classes.mediaContainer}>
      {localUrls?.map((signedMedia: SignedMedia, index: number) => (
        <div
          className={classes.mediaItem}
          key={media?.[index]?.id}
          onClick={openFile(signedMedia.signedUrl)}
        >
          {!signedMedia ? <ButtonSpinner /> : null}
          {signedMedia.isImage ? (
            <img className={classes.mediaImage} src={signedMedia.signedUrl} />
          ) : (
            <FileIcon />
          )}
        </div>
      ))}
    </div>
  )
}

export default memo(Media)
